import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Grid, Input, InputNumber, Select } from '@arco-design/web-react';

import type { CapabilityControlProps } from '../../core/types';
import { isFieldInteractionLocked } from '../../platform/capability-registry/fieldControl';
import { HTTP_PROBE_METHOD_OPTIONS, PROBE_TYPE_OPTIONS } from './constants';
import { HEALTH_CHECK_FIELD, HEALTH_CHECK_FIELD_KEYS } from './fieldKeys';
import type { HealthCheckBlockProps, ProbeType } from './types';

const { Row, Col } = Grid;
const { TextArea } = Input;

/** 探活配置能力块（编辑 containerDeployConfig.healthCheck 中会被 API 接收的字段） */
export function HealthCheckBlock({ form, paths, options, ...control }: HealthCheckBlockProps & CapabilityControlProps) {
  const { t } = useTranslation();
  const locked = isFieldInteractionLocked(HEALTH_CHECK_FIELD_KEYS.config, control);
  const configPath = paths.config || HEALTH_CHECK_FIELD;
  const defaultProbeType = Form.useWatch(`${configPath}.defaultProbeType`, form) as string | undefined;
  const supportedFromForm = Form.useWatch(`${configPath}.supportedProbeTypes`, form) as ProbeType[] | undefined;

  const probeTypeOptions = useMemo(() => {
    const types = options?.supportedProbeTypes ?? supportedFromForm ?? [];
    if (!types.length) return PROBE_TYPE_OPTIONS;
    return PROBE_TYPE_OPTIONS.filter((opt) => types.includes(opt.value));
  }, [options?.supportedProbeTypes, supportedFromForm]);

  return (
    <div>
      {/** enabled 由镜像 containerDeployConfig.healthCheck.enabled 驱动，外层 CheckExpandable / 模块勾选控制展示 */}
      <Form.Item field={`${configPath}.enabled`} hidden>
        <Input />
      </Form.Item>

      <Form.Item label={t('data-center:default.probe.type')} field={`${configPath}.defaultProbeType`}>
        <Select disabled={locked} options={probeTypeOptions} placeholder={t('data-center:please.select.default.probe.type')} />
      </Form.Item>
      {(defaultProbeType === 'http' || defaultProbeType === 'tcp') && (
        <Form.Item label={t('data-center:default.port')} field={`${configPath}.defaultPort`}>
          <InputNumber disabled={locked} min={1} max={65535} style={{ width: '100%' }} />
        </Form.Item>
      )}
      {defaultProbeType === 'http' && (
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label={t('data-center:default.path')} field={`${configPath}.defaultPath`}>
              <Input disabled={locked} placeholder="/healthz" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label={t('data-center:http.request.method')} field={`${configPath}.httpMethod`}>
              <Select
                disabled={locked}
                options={HTTP_PROBE_METHOD_OPTIONS}
                placeholder={t('data-center:please.select.default.probe.type')}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {defaultProbeType === 'exec' && (
        <Form.Item label={t('data-center:default.exec.command')} field={`${configPath}.defaultCommand`}>
          <TextArea
            disabled={locked}
            autoSize={{ minRows: 2, maxRows: 6 }}
            placeholder={t('data-center:please.enter.default.exec.command')}
          />
        </Form.Item>
      )}
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label={t('data-center:startup.wait.seconds')} field={`${configPath}.initialDelaySeconds`}>
            <InputNumber disabled={locked} min={0} max={3600} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('data-center:probe.check.interval.seconds')} field={`${configPath}.periodSeconds`}>
            <InputNumber disabled={locked} min={1} max={300} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label={t('data-center:probe.timeout.seconds')} field={`${configPath}.timeoutSeconds`}>
            <InputNumber disabled={locked} min={1} max={300} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('components:failure.threshold')} field={`${configPath}.failureThreshold`}>
            <InputNumber disabled={locked} min={1} max={20} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
}
